/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { submitContribution, updateUploadStatusPublic } from 'Api/apiFunctions';
import {
  createGiftUploadSession,
  completeGiftUploadSession,
  uploadPartWithPresignedUrl,
} from 'Api/uploaderApiFunctions';
import uploadManagerSecure from 'Utils/uploadManagerSecure';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import styles from './ContributeMobileUpload.module.css';

const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.m4v', '.mpeg', '.mpg'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];

// ── "Go to application" hand-off ─────────────────────────────────────────────
// Pinned banner + arrival popup (mobile only), matching the gift mobile-upload
// page. Reuses the SAME ChottuLink `contribute` smart link as the desktop
// /contribute page — we just add `mobileUpload=true` so the app knows to route
// to its contribute-mobile-upload screen instead of the normal contribute page.
// ChottuLink owns all routing: app installed → projector://contribute?...,
// not installed → App Store + deferred deep link, no app → web fallback.
const CHOTTULINK_DOMAIN = 'projectorapp.chottu.link';
const APP_DEEPLINK_PATH = 'contribute'; // → https://projectorapp.chottu.link/contribute?...&mobileUpload=true
const GALLERY_WARNING_TEXT = "Choosing files may take several minutes for it to upload. Please be patient while it's being uploaded. DO NOT PRESS CANCEL unless you want to stop the upload.";
const POPUP_MESSAGE_TEXT = "Choosing files may take several minutes for it to upload. Please be patient while it's being uploaded. You can upload from the Projector Mobile application. You don't need to sign up to upload images and videos.";

const changeSize = (size) => {
  const newSize = (size || 0) / 1024 / 1024;
  return newSize.toFixed(2);
};

// ── Video thumbnail capture (iOS-compatible) ────────────────────────────────
const captureVideoFrame = (file) => new Promise((resolve) => {
  const timeout = setTimeout(() => resolve(null), 6000);
  try {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    const cleanup = () => {
      try { URL.revokeObjectURL(videoUrl); } catch (e) { /* */ }
      try { video.remove(); } catch (e) { /* */ }
    };
    video.onloadedmetadata = () => {
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        clearTimeout(timeout); cleanup(); resolve(null); return;
      }
      video.currentTime = 0.5;
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;
        const ctx = canvas.getContext('2d');
        if (!ctx) { clearTimeout(timeout); cleanup(); resolve(null); return; }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        canvas.remove(); clearTimeout(timeout); cleanup();
        resolve(dataUrl && dataUrl.length > 1000 ? dataUrl : null);
      } catch (e) { clearTimeout(timeout); cleanup(); resolve(null); }
    };
    video.onerror = () => { clearTimeout(timeout); cleanup(); resolve(null); };
    video.src = videoUrl;
    video.load();
  } catch (e) { clearTimeout(timeout); resolve(null); }
});

// Generate 6 thumbnails from video (same as contribute page)
const generateThumbnailsFromVideo = (file) => new Promise((resolve) => {
  if (!file) { resolve({ thumbnails: [], videoHeight: 0, videoWidth: 0, videoDuration: 0 }); return; }
  let resolved = false;
  const fallback = { thumbnails: [], videoHeight: 0, videoWidth: 0, videoDuration: 0 };
  const video = document.createElement('video');
  const videoUrl = URL.createObjectURL(file);
  const cleanup = () => {
    try { URL.revokeObjectURL(videoUrl); } catch (e) { /* */ }
    try { video.remove(); } catch (e) { /* */ }
  };
  const timeout = setTimeout(() => { if (!resolved) { resolved = true; cleanup(); resolve(fallback); } }, 15000);
  video.preload = 'metadata'; video.muted = true; video.playsInline = true;
  video.src = videoUrl; video.load();
  video.onerror = () => { if (!resolved) { resolved = true; clearTimeout(timeout); cleanup(); resolve(fallback); } };
  video.onloadedmetadata = () => {
    if (resolved) return;
    const { duration, videoHeight, videoWidth } = video;
    if (videoWidth === 0 || videoHeight === 0) { resolved = true; clearTimeout(timeout); cleanup(); resolve(fallback); return; }
    const thumbnailCount = 6;
    const interval = duration / (thumbnailCount + 1);
    const tempThumbnails = [];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const maxWidth = 640;
    const scale = Math.min(1, maxWidth / videoWidth);
    canvas.width = videoWidth * scale;
    canvas.height = videoHeight * scale;
    let currentIndex = 0;
    const captureThumbnail = () => {
      if (resolved) return;
      if (currentIndex >= thumbnailCount) {
        resolved = true; clearTimeout(timeout); canvas.remove(); cleanup();
        resolve({ thumbnails: tempThumbnails, videoHeight, videoWidth, videoDuration: duration });
        return;
      }
      const time = Math.max(0.5, interval * (currentIndex + 1));
      video.currentTime = Math.min(time, duration - 0.1);
    };
    video.onseeked = () => {
      if (resolved) return;
      requestAnimationFrame(() => {
        if (resolved) return;
        try { ctx.drawImage(video, 0, 0, canvas.width, canvas.height); tempThumbnails.push(canvas.toDataURL('image/jpeg', 0.7)); } catch (e) { /* skip */ }
        currentIndex += 1;
        captureThumbnail();
      });
    };
    captureThumbnail();
  };
});

// Convert base64 to File for upload
const base64ToFile = (base64String, fileName) => {
  const [header, base64Data] = base64String.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64Data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) array[i] = binary.charCodeAt(i);
  return new File([array], fileName, { type: mime });
};

// Upload a single small file via gift session (1 part)
const uploadSingleFileGift = async (uuid, file, contentType) => {
  const session = await createGiftUploadSession(uuid, {
    file_name: file.name,
    file_size: file.size,
    content_type: contentType || file.type || 'application/octet-stream',
  });
  const presignedUrl = session.presigned_urls?.[0]?.presigned_url;
  if (!presignedUrl) throw new Error('No presigned URL returned');
  const partResult = await uploadPartWithPresignedUrl(presignedUrl, file, 1);
  const completeResponse = await completeGiftUploadSession(session.id, uuid, [partResult]);
  return completeResponse?.s3_key || completeResponse?.file_key || session.s3_key || session.file_key;
};

// Generate thumbnail for display
const generateThumbnail = async (file) => {
  if (!file.type.startsWith('video/')) {
    return file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
  }
  const frame = await captureVideoFrame(file);
  return frame || null;
};

const getAlbumGridClass = (count) => {
  if (count <= 4) return `${styles.albumFolderGrid} ${styles.albumFolderGrid2x2}`;
  if (count <= 6) return `${styles.albumFolderGrid} ${styles.albumFolderGrid3x2}`;
  return styles.albumFolderGrid;
};

// ════════════════════════════════════════════════════════════════════════════════
// Component
// ════════════════════════════════════════════════════════════════════════════════
const ContributeMobileUpload = () => {
  const params = new URLSearchParams(window.location.search);
  const tempToken = params.get('token');
  const uuid = params.get('uuid') || '';
  const sessionId = params.get('session') || '';

  const fileInputRef = useRef(null);
  const fileUploadBatchCounter = useRef(0);

  // ── Contributor name/email from URL (passed by desktop contribute page) ──
  const contributorName = params.get('name') || 'Contributor';
  const contributorEmail = params.get('email') || '';

  // Deep link is only useful on a phone, so gate the "Go to application" popup
  // on mobile (this page is reached by scanning the desktop QR).
  const isMobileDevice = /iphone|ipad|ipod|android/i.test(navigator.userAgent || '');

  // ── File & upload state ─────────────────────────────────────────────────
  const [files, setFiles] = useState([]);
  const [contentFilter, setContentFilter] = useState('all');
  const [showCompletePopup, setShowCompletePopup] = useState(false);
  const [albumModal, setAlbumModal] = useState(null);
  // "Go to application" arrival popup — offered on mobile before file picking.
  const [showGalleryWarning, setShowGalleryWarning] = useState(isMobileDevice);

  const cancelledIdsRef = useRef(new Set());
  const pausedIdsRef = useRef(new Map());
  const completedFileNamesRef = useRef([]);
  const completedS3KeysRef = useRef({});
  const completedThumbnailKeysRef = useRef({});
  // Accumulate photo thumbs across batches so allComplete can include them as fallback
  const photoThumbCacheRef = useRef({});

  // Warn before leaving during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (files.some((f) => f.status === UPLOAD_STATUS.UPLOADING)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [files]);

  // Subscribe to uploadManagerSecure for video progress
  useEffect(() => {
    const unsubscribe = uploadManagerSecure.subscribe(({ uploads: managerUploads }) => {
      if (managerUploads.length === 0) return;
      setFiles((prev) => {
        let changed = false;
        const updated = prev.map((f) => {
          if (!f.secureUploadId) return f;
          const mu = managerUploads.find((u) => u.id === f.secureUploadId);
          if (!mu) return f;
          let mappedStatus = f.status;
          if (mu.status === 'pending') mappedStatus = UPLOAD_STATUS.PENDING;
          else if (mu.status === 'uploading' || mu.status === 'initializing' || mu.status === 'finishing') mappedStatus = UPLOAD_STATUS.UPLOADING;
          else if (mu.status === 's3_complete' || mu.status === 'completed') mappedStatus = UPLOAD_STATUS.COMPLETED;
          else if (mu.status === 'paused') mappedStatus = UPLOAD_STATUS.PAUSED;
          else if (mu.status === 'failed') mappedStatus = UPLOAD_STATUS.FAILED;
          else if (mu.status === 'cancelled') mappedStatus = UPLOAD_STATUS.CANCELLED;
          const managerProgress = mu.status === 's3_complete' ? 100 : Math.round((mu.progress || 0) * 0.9);
          changed = true;
          return { ...f, status: mappedStatus, progress: Math.max(managerProgress, f.progress || 0), s3Key: mu.s3Key || f.s3Key };
        });
        return changed ? updated : prev;
      });
    });
    return unsubscribe;
  }, []);

  // ── SSE send helper (token-based, same as mobile upload screen) ─────────
  const sendStatus = useCallback(async (statusData) => {
    if (!sessionId) return;
    try {
      await updateUploadStatusPublic({
        session_id: sessionId,
        status_data: JSON.stringify({ ...statusData, contribution: true }),
        token: tempToken,
      });
    } catch (err) {
      console.error('[ContributeMobile] Failed to send status:', err);
    }
  }, [sessionId, tempToken]);

  // ── Display files (group photos into album) ────────────────────────────
  const displayFiles = useMemo(() => {
    const videos = files.filter((f) => f.type === 'video');
    const photos = files.filter((f) => f.type === 'image');
    if (photos.length === 0) return videos;
    const completedPhotos = photos.filter((f) => f.status === UPLOAD_STATUS.COMPLETED).length;
    const failedPhotos = photos.filter((f) => f.status === UPLOAD_STATUS.FAILED).length;
    const allDone = completedPhotos + failedPhotos === photos.length;
    const albumProgress = photos.length > 0 ? Math.round((completedPhotos / photos.length) * 100) : 0;
    const albumEntry = {
      id: 'photo-album',
      name: `All photos (${photos.length})`,
      type: 'album',
      status: allDone ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING,
      progress: albumProgress,
      thumbnailUrl: photos[0]?.thumbnailUrl || null,
      totalPhotos: photos.length,
      uploadedPhotos: completedPhotos,
      isPhotoAlbum: true,
      photoIds: photos.map((p) => p.id),
      photos,
    };
    return [...videos, albumEntry];
  }, [files]);

  const filteredFiles = useMemo(() => {
    if (contentFilter === 'all') return displayFiles;
    if (contentFilter === 'videos') return displayFiles.filter((f) => f.type === 'video');
    if (contentFilter === 'photos') return files.filter((f) => f.type === 'image');
    return displayFiles;
  }, [displayFiles, files, contentFilter]);

  const videoCount = files.filter((f) => f.type === 'video').length;
  const photoCount = files.filter((f) => f.type === 'image').length;

  // ── Upload a single video (same method as contribute page) ──────────────
  const uploadVideo = useCallback(async (fileEntry) => {
    const { id, file, name } = fileEntry;
    const contName = contributorName.trim() || localStorage.getItem('contributorsName') || 'Contributor';

    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: UPLOAD_STATUS.UPLOADING, isThumbnailUploading: true } : f)));

    // 1. Generate thumbnails
    const thumbData = await generateThumbnailsFromVideo(file);
    const thumbnailS3Keys = [];
    for (let i = 0; i < thumbData.thumbnails.length; i += 1) {
      try {
        const thumbFile = base64ToFile(thumbData.thumbnails[i], `thumb_${i}.jpg`);
        // eslint-disable-next-line no-await-in-loop
        const s3Key = await uploadSingleFileGift(uuid, thumbFile, 'image/jpeg');
        thumbnailS3Keys.push(s3Key);
      } catch (err) { console.error(`[ContributeMobile] Thumbnail ${i} upload failed:`, err); }
    }

    // 2. Upload video via uploadLargeFile with giftUuid. draft_id must be a
    // generated UUID (same convention as the main video upload flow) — the
    // submit API expects it in that form.
    const draftId = uuidv4();
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, secureUploadId: draftId, isThumbnailUploading: false } : f)));

    return new Promise((resolveUpload) => {
      uploadLargeFile({
        userId: uuid,
        file,
        draftId,
        thumbnails: [],
        suppressToast: true,
        giftUuid: uuid,
        onProgress: (progress) => {
          const progressPercent = Math.round(progress * 0.9);
          setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, progress: Math.max(progressPercent, f.progress || 0) } : f)));
        },
        onSuccess: null,
        onError: (error) => {
          console.error('[ContributeMobile] Video upload error:', error);
          setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: UPLOAD_STATUS.FAILED } : f)));
          resolveUpload({ success: false, error });
        },
      });

      // Poll for s3_complete
      const pollInterval = setInterval(() => {
        const status = uploadManagerSecure.getUploadStatus?.(draftId);
        if (status === 's3_complete' || status === 'completed') {
          clearInterval(pollInterval);
          const managerUpload = uploadManagerSecure.getUpload?.(draftId);
          const videoS3Key = managerUpload?.s3Key || '';
          const videoOrientation = thumbData.videoWidth > thumbData.videoHeight ? '1' : '2';

          submitContribution({
            uuid,
            temp_token: tempToken,
            email: contributorEmail.trim(),
            name: contName,
            type: 'video',
            payload: {
              method: 'POST',
              endpoint: '/V6/addVideo',
              action_id: `vid_${draftId}`,
              action_type: 'addVideo',
              payload: {
                draft_id: draftId,
                // Videos are submitted without a title — the owner titles the
                // item later after approval (mirrors the Flutter app and the
                // /contribute page). Only videos; albums keep their title.
                title: '',
                category_id: '',
                category_name: `${contName}'s videos`,
                file_name: name,
                file_key: videoS3Key,
                document_id: '',
                bucket_folder_path: videoS3Key,
                description: '',
                video_size: changeSize(file.size),
                group_ids: JSON.stringify([]),
                visibility: '1',
                subcategory_id: '',
                playlist_ids: '',
                videoHeight: String(thumbData.videoHeight || ''),
                videoWidth: String(thumbData.videoWidth || ''),
                videoOreintation: videoOrientation,
                s3_status: 'completed',
                thumbnail: thumbnailS3Keys[0] || '',
                thumbnails: thumbnailS3Keys,
              },
            },
          }).then(() => {
            setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: UPLOAD_STATUS.COMPLETED, progress: 100 } : f)));
            completedFileNamesRef.current.push(`${name}|${id}`);
            completedS3KeysRef.current[id] = videoS3Key;
            const [firstThumbKey] = thumbnailS3Keys;
            if (firstThumbKey) completedThumbnailKeysRef.current[id] = firstThumbKey;
            sendStatus({
              action: 'fileComplete',
              files: [...completedFileNamesRef.current],
              s3Keys: { ...completedS3KeysRef.current },
              thumbnailKeys: { ...completedThumbnailKeysRef.current },
            });
            resolveUpload({ success: true });
          }).catch((err) => {
            console.error('[ContributeMobile] Submit error:', err);
            setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: UPLOAD_STATUS.FAILED } : f)));
            resolveUpload({ success: false, error: err });
          });
        } else if (status === 'failed' || status === 'cancelled') {
          clearInterval(pollInterval);
          setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, status: UPLOAD_STATUS.FAILED } : f)));
          resolveUpload({ success: false });
        }
      }, 500);
    });
  }, [uuid, tempToken, contributorName, contributorEmail, sendStatus]);

  // ── Upload photos as album (same method as contribute page) ─────────────
  const uploadPhotoAlbum = useCallback(async (photoEntries) => {
    if (photoEntries.length === 0) return;
    const contName = contributorName.trim() || localStorage.getItem('contributorsName') || 'Contributor';
    const photoS3Data = [];

    for (let i = 0; i < photoEntries.length; i += 1) {
      const entry = photoEntries[i];
      if (cancelledIdsRef.current.has(entry.id)) continue; // eslint-disable-line no-continue
      setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: UPLOAD_STATUS.UPLOADING, progress: 0 } : f)));
      try {
        // eslint-disable-next-line no-await-in-loop
        const session = await createGiftUploadSession(uuid, {
          file_name: entry.name, file_size: entry.file.size, content_type: entry.file.type || 'image/jpeg',
        });
        const presignedUrl = session.presigned_urls?.[0]?.presigned_url;
        if (!presignedUrl) throw new Error('No presigned URL for photo');
        // eslint-disable-next-line no-await-in-loop
        const partResult = await uploadPartWithPresignedUrl(presignedUrl, entry.file, 1);
        // eslint-disable-next-line no-await-in-loop
        const completeResponse = await completeGiftUploadSession(session.id, uuid, [partResult]);
        const s3Key = completeResponse?.s3_key || completeResponse?.file_key || session.s3_key || session.file_key;
        photoS3Data.push({ path: s3Key, size: String(entry.file.size), name: entry.name });
        setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: UPLOAD_STATUS.COMPLETED, progress: 100 } : f)));

        // SSE per photo
        completedFileNamesRef.current.push(`${entry.name}|${entry.id}`);
        // eslint-disable-next-line no-await-in-loop
        await sendStatus({ action: 'fileComplete', files: [...completedFileNamesRef.current], s3Keys: { ...completedS3KeysRef.current } });
      } catch (err) {
        console.error('[ContributeMobile] Photo upload error:', entry.name, err);
        setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: UPLOAD_STATUS.FAILED } : f)));
      }
    }

    if (photoS3Data.length === 0) return;

    // Submit album contribution
    try {
      await submitContribution({
        uuid,
        temp_token: tempToken,
        email: contributorEmail.trim(),
        name: contName,
        type: 'album',
        payload: {
          method: 'POST',
          endpoint: '/createAndAttachAlbum',
          action_id: `album_${Date.now()}`,
          action_type: 'createAndAttachAlbum',
          payload: {
            icon: photoS3Data[0]?.path || '',
            title: `${contName}'s Album`,
            photos: photoS3Data,
            group_id: '',
            visibility: '2',
            description: '',
          },
        },
      });
    } catch (err) {
      console.error('[ContributeMobile] Album submit error:', err);
    }
  }, [uuid, tempToken, contributorName, contributorEmail, sendStatus]);

  // ── Handle file select ──────────────────────────────────────────────────
  const handleFileSelect = useCallback(async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    e.target.value = '';

    fileUploadBatchCounter.current += 1;
    const batchId = fileUploadBatchCounter.current;

    const newFiles = selectedFiles.map((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      return {
        id: uuidv4(),
        file,
        name: file.name,
        size: file.size,
        type: VIDEO_EXTENSIONS.includes(ext) ? 'video' : 'image',
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        thumbnailUrl: IMAGE_EXTENSIONS.includes(ext) ? URL.createObjectURL(file) : null,
        previewUrl: VIDEO_EXTENSIONS.includes(ext) ? URL.createObjectURL(file) : null,
      };
    });

    setFiles((prev) => [...prev, ...newFiles]);

    // Separate videos and photos
    const videoFiles = newFiles.filter((f) => f.type === 'video');
    const photoFiles = newFiles.filter((f) => f.type === 'image');

    // Generate video thumbnails in background
    videoFiles.forEach(async (f) => {
      const thumbUrl = await generateThumbnail(f.file);
      if (thumbUrl) setFiles((prev) => prev.map((item) => (item.id === f.id ? { ...item, thumbnailUrl: thumbUrl } : item)));
    });

    // Generate micro thumbnails for photos to send to desktop (same as mobile-upload page)
    const thumbs = {};
    await Promise.all(photoFiles.map((f) => new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(), 2000);
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          try {
            const maxWidth = 240;
            const scale = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              thumbs[f.name] = canvas.toDataURL('image/jpeg', 0.7);
            }
            canvas.remove();
          } catch (e) { /* ignore */ }
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => { clearTimeout(timeout); resolve(); };
        img.src = reader.result;
      };
      reader.onerror = () => { clearTimeout(timeout); resolve(); };
      reader.readAsDataURL(f.file);
    })));

    // Cache photo thumbs across batches for allComplete fallback
    Object.entries(thumbs).forEach(([name, thumb]) => {
      if (thumb) photoThumbCacheRef.current[name] = thumb;
    });

    // Send SSE file notification (include photo thumbs)
    const fileNames = newFiles.map((f) => `${f.name}|${f.id}`);
    const statusPayload = { action: `fileUpload${batchId}`, files: fileNames, fileCount: newFiles.length };
    if (Object.keys(thumbs).length > 0) {
      statusPayload.thumbs = thumbs;
    }
    await sendStatus(statusPayload);
    const uploadPromises = [];
    if (videoFiles.length > 0) uploadPromises.push(Promise.all(videoFiles.map((f) => uploadVideo(f))));
    if (photoFiles.length > 0) uploadPromises.push(uploadPhotoAlbum(photoFiles));
    await Promise.all(uploadPromises);

    const allCompletePayload = {
      action: 'allComplete',
      files: [...completedFileNamesRef.current],
      s3Keys: { ...completedS3KeysRef.current },
      thumbnailKeys: { ...completedThumbnailKeysRef.current },
      fileCount: newFiles.filter((f) => f.status !== 'failed').length,
    };
    // Include accumulated photo thumbs as fallback for missed fileUpload SSEs
    if (Object.keys(photoThumbCacheRef.current).length > 0) {
      allCompletePayload.thumbs = { ...photoThumbCacheRef.current };
    }
    await sendStatus(allCompletePayload);
  }, [uploadVideo, uploadPhotoAlbum, sendStatus]);

  const hasFiles = files.length > 0;
  const isUploading = (status) => status === UPLOAD_STATUS.UPLOADING || status === UPLOAD_STATUS.PENDING;

  const handlePauseUpload = (fileId) => {
    pausedIdsRef.current.set(fileId, null);
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: UPLOAD_STATUS.PAUSED } : f)));
  };

  const handleResumeUpload = (fileId) => {
    const resolve = pausedIdsRef.current.get(fileId);
    pausedIdsRef.current.delete(fileId);
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: UPLOAD_STATUS.UPLOADING } : f)));
    if (resolve) resolve();
  };

  const handleCancelUpload = (fileId) => {
    cancelledIdsRef.current.add(fileId);
    const pauseResolve = pausedIdsRef.current.get(fileId);
    if (pauseResolve) pauseResolve();
    pausedIdsRef.current.delete(fileId);
    setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: UPLOAD_STATUS.CANCELLED } : f)));
    setTimeout(() => { setFiles((prev) => prev.filter((f) => f.id !== fileId)); cancelledIdsRef.current.delete(fileId); }, 1000);
  };

  // Hand off to the native app via the shared ChottuLink `contribute` smart
  // link. We forward the full current query string (token/uuid/session/name/
  // email) and add `mobileUpload=true` so the app routes to its
  // contribute-mobile-upload screen and pushes progress to the same SSE session.
  const handleOpenApp = () => {
    const linkParams = new URLSearchParams(window.location.search);
    linkParams.set('mobileUpload', 'true');
    const query = linkParams.toString();
    const smartLink = `https://${CHOTTULINK_DOMAIN}/${APP_DEEPLINK_PATH}${query ? `?${query}` : ''}`;
    window.location.href = smartLink;
  };

  // ══════════════════════════════════════════════════════════════════════════
  // Error state
  // ══════════════════════════════════════════════════════════════════════════
  if (!tempToken || !uuid) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="#EF4444">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p className={styles.errorStateText}>Invalid link. Please scan the QR code again.</p>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // JSX
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className={styles.container}>
      {/* Pinned reminder of the gallery slowness warning (same text as the
          arrival popup) so the user keeps it in mind while picking files. */}
      <div className={styles.galleryWarningBanner}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#f5b53f" aria-hidden="true">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
        <span>{GALLERY_WARNING_TEXT}</span>
      </div>

      {/* ── File picker ────────────────────────────────────────────────────── */}
      {!hasFiles && (
        <div className={styles.mobileUploadCenter}>
          <div
            aria-hidden="true"
            className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={styles.uploadOptionIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className={styles.uploadOptionText}>Choose Files from Gallery</span>
          </div>
          <input ref={fileInputRef} type="file" accept="video/*,image/*" multiple onChange={handleFileSelect} className={styles.fileInput} />
        </div>
      )}

      {/* ── Upload progress ────────────────────────────────────────────────── */}
      {hasFiles && (
        <div className={styles.selectedFilesContainer}>
          <div className={styles.quickAddRow}>
            <div className={styles.contentFilterTabs}>
              {[{ label: 'All', value: 'all' }, { label: 'Videos', value: 'videos' }, { label: 'Photos', value: 'photos' }].map((tab) => (
                <button key={tab.value} type="button" className={`${styles.contentFilterTab}${contentFilter === tab.value ? ` ${styles.contentFilterTabActive}` : ''}`} onClick={() => setContentFilter(tab.value)}>
                  {tab.label}
                </button>
              ))}
            </div>
            <div className={styles.quickAddRight}>
              <p className={styles.addMoreLabel}>Add More Media</p>
              <div className={styles.quickAddIcons}>
                <div role="button" tabIndex={0} className={styles.quickAddOption} onClick={() => fileInputRef.current?.click()} onKeyDown={(e) => { if (e.key === 'Enter') fileInputRef.current?.click(); }}>
                  <svg className={styles.quickAddSvg} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept="video/*,image/*" multiple onChange={handleFileSelect} className={styles.fileInput} />

          <div className={`${styles.uploadProgressList}${contentFilter !== 'all' ? ` ${styles.filteredGrid}` : ''}`}>
            {filteredFiles.length === 0 ? (
              <div className={styles.emptyFilter}>{contentFilter === 'videos' ? 'No videos' : 'No photos'}</div>
            ) : contentFilter !== 'all' ? (
              filteredFiles.map((f) => (
                <div key={f.id} className={`${styles.filteredCard}${isUploading(f.status) ? ` ${styles.filteredCardUploading}` : ''}`}>
                  {/* Album: dynamic photo grid thumbnail */}
                  {f.isPhotoAlbum && f.photos && f.photos.length > 0 ? (
                    <div className={getAlbumGridClass(f.photos.length)}>
                      {f.photos.slice(0, f.photos.length > 9 ? 8 : 9).map((photo, idx) => (
                        <div key={photo.id || idx}>
                          {photo.thumbnailUrl ? <img src={photo.thumbnailUrl} alt={photo.name || `Photo ${idx + 1}`} /> : null}
                        </div>
                      ))}
                      {f.photos.length > 9 && (
                        <div className={styles.albumFolderGridCount}>{`+${f.photos.length - 8}`}</div>
                      )}
                      {f.status === UPLOAD_STATUS.COMPLETED && (
                        <button type="button" className={styles.filteredPreviewEyeButton} onClick={() => setAlbumModal({ photos: files.filter((p) => p.type === 'image') })}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" /></svg>
                        </button>
                      )}
                      {isUploading(f.status) && (
                        <>
                          <div className={styles.filteredCardShimmer} />
                          <div className={styles.filteredCardProgress}>
                            <div className={styles.filteredCardProgressIndeterminate} />
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className={styles.filteredCardThumb}>
                      {f.thumbnailUrl ? <img src={f.thumbnailUrl} alt={f.name} />
                        : f.type === 'video' && f.previewUrl ? <video src={f.previewUrl} muted playsInline preload="metadata" />
                          : <div className={styles.filteredCardPlaceholder}>{f.type === 'video' ? <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg> : <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>}</div>}
                      {isUploading(f.status) && (<><div className={styles.filteredCardShimmer} /><div className={styles.filteredCardProgress}><div className={styles.filteredCardProgressBar} style={{ width: `${f.progress}%` }} /></div></>)}
                    </div>
                  )}
                  <div className={styles.filteredCardInfo}>
                    <div className={styles.filteredCardTitleRow}>
                      {/* Videos never show a title (mirrors /contribute); albums/photos keep theirs. */}
                      {f.type !== 'video' && (
                        <span className={styles.filteredCardTitle}>{f.name.length > 18 ? `${f.name.substring(0, 18)}...` : f.name}</span>
                      )}
                      {!isUploading(f.status) && (
                        <span className={styles.filteredCardTypeIcon}>
                          {f.type === 'video' ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              filteredFiles.map((f) => (
                <div key={f.id} className={`${styles.uploadProgressItem}${f.status === UPLOAD_STATUS.PENDING ? ` ${styles.previewLoading}` : ''}`}>
                  {/* Album: dynamic photo grid thumbnail */}
                  {f.isPhotoAlbum && f.photos && f.photos.length > 0 ? (
                    <div className={getAlbumGridClass(f.photos.length)}>
                      {f.photos.slice(0, f.photos.length > 9 ? 8 : 9).map((photo, idx) => (
                        <div key={photo.id || idx}>
                          {photo.thumbnailUrl ? <img src={photo.thumbnailUrl} alt={photo.name || `Photo ${idx + 1}`} /> : null}
                        </div>
                      ))}
                      {f.photos.length > 9 && (
                        <div className={styles.albumFolderGridCount}>{`+${f.photos.length - 8}`}</div>
                      )}
                      {f.status === UPLOAD_STATUS.COMPLETED && (
                        <button type="button" className={styles.filteredPreviewEyeButton} onClick={() => setAlbumModal({ photos: files.filter((p) => p.type === 'image') })}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" /></svg>
                        </button>
                      )}
                      {isUploading(f.status) && (
                        <>
                          <div className={styles.filteredCardShimmer} />
                          <div className={styles.filteredCardProgress}>
                            <div className={styles.filteredCardProgressIndeterminate} />
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className={styles.filteredCardThumb}>
                      {f.thumbnailUrl ? <img src={f.thumbnailUrl} alt={f.name} />
                        : f.type === 'video' && f.previewUrl ? <video src={f.previewUrl} muted playsInline preload="metadata" />
                          : <div className={styles.filteredCardPlaceholder}><div className={styles.shimmerAnimation} /></div>}
                    </div>
                  )}
                  <div className={styles.cardInfo}>
                    <div className={styles.uploadProgressHeader}>
                      <div className={styles.filteredCardTitleRow}>
                        {/* Videos never show a title (mirrors /contribute); albums/photos keep theirs. */}
                        {f.type !== 'video' && (
                          <span className={styles.uploadFileName}>{f.name.length > 25 ? `${f.name.substring(0, 25)}...` : f.name}</span>
                        )}
                        {f.status === UPLOAD_STATUS.COMPLETED && (
                          <span className={styles.filteredCardTypeIcon}>
                            {f.type === 'video' ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" /></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>}
                          </span>
                        )}
                      </div>
                      {f.isPhotoAlbum && f.status === UPLOAD_STATUS.UPLOADING && (
                        <div className={styles.uploadProgressDetails}><span>{f.uploadedPhotos || 0} / {f.totalPhotos || 0} photos uploaded</span></div>
                      )}
                      <div className={styles.uploadStatusActions}>
                        <span className={styles.uploadStatus}>
                          {f.status === UPLOAD_STATUS.FAILED && <button type="button" onClick={() => handleCancelUpload(f.id)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', font: 'inherit', padding: 0 }} title="Remove">✕</button>}
                          {f.status === UPLOAD_STATUS.UPLOADING && f.isPhotoAlbum && `${f.uploadedPhotos || 0}/${f.totalPhotos || 0} photos`}
                          {f.status === UPLOAD_STATUS.UPLOADING && !f.isPhotoAlbum && f.isThumbnailUploading && (
                            <span className={styles.mobileUploadStatus}>
                              Uploading thumbnails...
                              <span className={styles.mobileUploadSpinner} />
                            </span>
                          )}
                          {f.status === UPLOAD_STATUS.UPLOADING && !f.isPhotoAlbum && !f.isThumbnailUploading && `${f.progress}%`}
                          {f.status === UPLOAD_STATUS.PAUSED && 'Paused'}
                          {f.status === UPLOAD_STATUS.PENDING && 'Waiting...'}
                          {f.status === UPLOAD_STATUS.CANCELLED && 'Cancelled'}
                          {f.status === UPLOAD_STATUS.COMPLETED && <span className={styles.uploadCompleteText}>Complete</span>}
                        </span>
                        {!f.isPhotoAlbum && !f.isThumbnailUploading && (f.status === UPLOAD_STATUS.UPLOADING || f.status === UPLOAD_STATUS.PAUSED) && (
                          <div className={styles.uploadControls}>
                            {f.status === UPLOAD_STATUS.UPLOADING && (<button type="button" className={styles.uploadControlButton} onClick={() => handlePauseUpload(f.id)} title="Pause"><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /></svg></button>)}
                            {f.status === UPLOAD_STATUS.PAUSED && (<button type="button" className={styles.uploadControlButton} onClick={() => handleResumeUpload(f.id)} title="Resume"><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></button>)}
                            <button type="button" className={styles.uploadControlButtonCancel} onClick={() => handleCancelUpload(f.id)} title="Cancel"><svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg></button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.uploadProgressBarContainer}>
                      <div className={`${styles.uploadProgressBar}${f.status === UPLOAD_STATUS.COMPLETED ? ` ${styles.uploadProgressComplete}` : ''}${f.status === UPLOAD_STATUS.FAILED ? ` ${styles.uploadProgressFailed}` : ''}${f.isThumbnailUploading && f.status === UPLOAD_STATUS.UPLOADING ? ` ${styles.uploadProgressIndeterminate}` : ''}`} style={{ width: f.isThumbnailUploading && f.status === UPLOAD_STATUS.UPLOADING ? '100%' : `${f.progress}%` }} />
                    </div>
                    <div className={styles.uploadProgressDetails}>
                      {f.status === UPLOAD_STATUS.COMPLETED && <span className={styles.uploadCompleteText}>Complete</span>}
                      {f.status === UPLOAD_STATUS.FAILED && <span className={styles.uploadFailedText}>Failed</span>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className={styles.uploadActionsRow}>
            <button type="button" className={styles.continueButton} disabled={files.length === 0 || !files.every((f) => f.status === UPLOAD_STATUS.COMPLETED || f.status === UPLOAD_STATUS.FAILED)} onClick={() => setShowCompletePopup(true)}>
              Continue
            </button>
            <p className={styles.continueSubText}>Please stay on the screen until uploads are fully completed</p>
            <span className={styles.filesCount}>{`${videoCount} video${videoCount !== 1 ? 's' : ''}, ${photoCount} photo${photoCount !== 1 ? 's' : ''}`}</span>
          </div>
        </div>
      )}

      {/* Album modal */}
      {albumModal && (
        <div className={styles.albumGridOverlay} role="button" tabIndex={0} onClick={() => setAlbumModal(null)} onKeyDown={(e) => { if (e.key === 'Escape') setAlbumModal(null); }}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div role="dialog" aria-modal="true" className={styles.albumGridModal} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button type="button" className={styles.albumGridModalClose} onClick={() => setAlbumModal(null)}>&times;</button>
            <div className={styles.albumGridModalHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#5aa5ef"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
              {`All photos (${albumModal.photos?.length || 0} photos)`}
            </div>
            <div className={styles.albumGridModalBody}>
              {albumModal.photos?.length > 0 ? (
                <div className={styles.albumGridPhotos}>
                  {albumModal.photos.map((photo) => (
                    <div key={photo.id} className={styles.albumGridPhotoItem}>
                      {photo.thumbnailUrl ? <img src={photo.thumbnailUrl} alt={photo.name} /> : <div className={styles.albumGridPhotoPlaceholder}><svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg></div>}
                      <span className={styles.albumGridPhotoName}>{photo.name}</span>
                    </div>
                  ))}
                </div>
              ) : <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No photos</p>}
            </div>
          </div>
        </div>
      )}

      {/* Complete popup */}
      {/* Arrival popup (mobile) — offer the native app, or continue in browser. */}
      {showGalleryWarning && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupCard}>
            <div className={styles.popupWarnIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#f5b53f" aria-hidden="true">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
            </div>
            <h3 className={styles.popupTitle}>Attention</h3>
            <p className={styles.popupMessage}>{POPUP_MESSAGE_TEXT}</p>
            <button type="button" className={styles.popupButton} style={{ paddingTop: 0, paddingBottom: 0 }} onClick={handleOpenApp}>
              GO TO APPLICATION
            </button>
            <button type="button" className={styles.popupSecondaryButton} style={{ paddingTop: 0, paddingBottom: 0 }} onClick={() => setShowGalleryWarning(false)}>
              CONTINUE HERE
            </button>
          </div>
        </div>
      )}

      {showCompletePopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupCard}>
            <div className={styles.popupCheckmark}>&#10003;</div>
            <h3 className={styles.popupTitle}>Completed</h3>
            <p className={styles.popupMessage}>Your files have been uploaded successfully. You can close this page now.</p>
            <button type="button" className={styles.popupButton} onClick={() => { window.close(); window.location.href = 'about:blank'; }}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContributeMobileUpload;
